import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  Box,
  TextField,
  Popper,
  Paper,
  Grid,
  Tooltip,
  Badge,
  InputAdornment,
  CircularProgress,
  ClickAwayListener
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import iconData from "../../database/category-icons.json";
import {
  IconSelectorContainer,
  IconPreview,
  IconGrid,
  IconItem,
  IconLabel,
  SearchBox
} from "./styles";
import { useTranslation } from "react-i18next";

interface IconOption {
  name: string;
  label: string;
  svg: string;
}

interface IconSelectorProps {
  value?: string; // SVG string value
  onChange?: (svg: string, iconName: string, label: string) => void;
  placeholder?: string;
  label?: string;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
}

const IconSelector: React.FC<IconSelectorProps> = ({
  value = "",
  onChange,
  placeholder = "Select an icon...",
  label = "Icon",
  error = false,
  helperText = "",
  disabled = false
}) => {
  const [searchValue, setSearchValue] = useState("");
  const [anchorEl, setAnchorEl] = useState<null | HTMLDivElement>(null);
  const [selectedIcon, setSelectedIcon] = useState<IconOption | null>(null);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const open = Boolean(anchorEl);

  // Filter icons based on search value
  const filteredIcons = useMemo(() => {
    if (!searchValue.trim()) return iconData;
    const searchLower = searchValue.toLowerCase();
    return iconData.filter(
      icon =>
        icon.name.toLowerCase().includes(searchLower) ||
        icon.label.toLowerCase().includes(searchLower)
    );
  }, [searchValue]);

  // Find and set selected icon on component mount or when value changes
  useEffect(() => {
    if (value) {
      const found = iconData.find(icon => icon.svg === value);
      if (found) {
        setSelectedIcon(found);
      } else {
        // If SVG not found, clear selection
        setSelectedIcon(null);
      }
    } else {
      setSelectedIcon(null);
    }
  }, [value]);

  const handleOpenDropdown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!disabled) {
      setAnchorEl(e.currentTarget);
      setSearchValue("");
    }
  };

  const handleCloseDropdown = () => {
    setAnchorEl(null);
    setSearchValue("");
  };

  const handleIconSelect = (icon: IconOption) => {
    setSelectedIcon(icon);
    if (onChange) {
      onChange(icon.svg, icon.name, icon.label);
    }
    handleCloseDropdown();
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedIcon(null);
    if (onChange) {
      onChange("", "", "");
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      handleCloseDropdown();
    }
  };

  return (
    <ClickAwayListener onClickAway={handleCloseDropdown}>
      <IconSelectorContainer ref={containerRef}>
        <Box
          onClick={handleOpenDropdown}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            padding: "8px 12px",
            border: error ? "1px solid #d32f2f" : "1px solid #ccc",
            borderRadius: "4px",
            backgroundColor: disabled ? "#f5f5f5" : "white",
            cursor: disabled ? "default" : "pointer",
            transition: "border-color 0.2s, box-shadow 0.2s",
            boxShadow: open ? "0 0 0 2px rgba(25, 118, 210, 0.2)" : "none",
            "&:hover": disabled
              ? {}
              : {
                  borderColor: open ? "#1976d2" : "#999"
                }
          }}
        >
        {/* Icon Preview */}
        {selectedIcon ? (
          <IconPreview
            dangerouslySetInnerHTML={{ __html: selectedIcon.svg }}
            title={selectedIcon.label}
          />
        ) : (
          <Box
            sx={{
              width: 24,
              height: 24,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#999"
            }}
          >
            <SearchIcon sx={{ fontSize: 18 }} />
          </Box>
        )}

        {/* Text */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <span
            style={{
              fontSize: "14px",
              color: selectedIcon ? "#333" : "#999",
              textOverflow: "ellipsis",
              overflow: "hidden",
              whiteSpace: "nowrap",
              display: "block"
            }}
          >
            {selectedIcon ? selectedIcon.label : placeholder}
          </span>
        </Box>

        {/* Clear Button */}
        {selectedIcon && !disabled && (
          <Box
            onClick={handleClear}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              "&:hover": {
                color: "#d32f2f"
              }
            }}
          >
            <CloseIcon sx={{ fontSize: 18, color: "#999" }} />
          </Box>
        )}
      </Box>

      {/* Helper Text / Error */}
      {helperText && (
        <span
          style={{
            fontSize: "12px",
            color: error ? "#d32f2f" : "#666",
            marginTop: "4px",
            display: "block"
          }}
        >
          {helperText}
        </span>
      )}

      {/* Dropdown Popper */}
      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="bottom-start"
        style={{
          zIndex: 1300,
          width: anchorEl?.clientWidth,
          marginTop: "8px"
        }}
        modifiers={[
          {
            name: "offset",
            options: {
              offset: [0, 8]
            }
          }
        ]}
      >
        <Paper
          sx={{
            width: anchorEl?.clientWidth || 300,
            maxWidth: "100%",
            boxShadow: "0 4px 16px rgba(0, 0, 0, 0.15)"
          }}
        >
          {/* Search Input */}
          <SearchBox>
            <TextField
              autoFocus
              fullWidth
              size="small"
              placeholder={t("Management.WorkflowIconPlaceholder")}
              value={searchValue}
              onChange={handleSearchChange}
              onKeyDown={handleKeyDown}
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: "#999" }} />
                  </InputAdornment>
                )
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 0,
                  fontSize: "13px"
                }
              }}
            />
          </SearchBox>

          {/* Icons Grid */}
          <Box sx={{ maxHeight: "400px", overflowY: "auto", padding: "8px" }}>
            {filteredIcons.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: 100,
                  color: "#999",
                  fontSize: "13px"
                }}
              >
                No icons found
              </Box>
            ) : (
              <Grid container spacing={0.5}>
                {filteredIcons.map(icon => (
                  <Grid item xs={3} sm={3} key={icon.name}>
                    <Tooltip title={icon.label} placement="top" arrow>
                      <IconItem
                        onClick={() => handleIconSelect(icon)}
                        isSelected={selectedIcon?.name === icon.name}
                      >
                        <Box
                          component="div"
                          dangerouslySetInnerHTML={{ __html: icon.svg }}
                          sx={{
                            "& svg": {
                              width: 24,
                              height: 24
                            }
                          }}
                        />
                        <IconLabel>{icon.label}</IconLabel>
                      </IconItem>
                    </Tooltip>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        </Paper>
      </Popper>
      </IconSelectorContainer>
    </ClickAwayListener>
  );
};

export default IconSelector;
